import type { CrowdexUIMessage } from '@crowdex/agent-contracts';
import { parseEditedMessage } from '@/lib/desktop-shell/edited-message';
import type { DesktopShellStore } from '@/lib/desktop-shell/store';
import { flushPendingComposerDraft } from '@/lib/desktop-shell/use-composer-draft';

export type MessageEditControls = {
  editMessage: (message: CrowdexUIMessage, text: string) => void;
  cancelMessageEdit: () => void;
};

/**
 * Enter/exit the "edit a previous message" composer state: load the message
 * back into the composer (re-hydrating browser selections and annotations with
 * fresh ids), or clear it. Pure store-backed; editing is a no-op while the agent
 * is busy. Extracted from inline handlers in `DesktopShell`.
 */
export function createMessageEditControls({
  store,
  busy,
}: {
  store: DesktopShellStore;
  busy: boolean;
}): MessageEditControls {
  function setDraft(value: string): void {
    const { chatId, setPaneDrafts } = store.getState();
    flushPendingComposerDraft(store, chatId);
    setPaneDrafts((current) => ({ ...current, [chatId]: value }));
  }

  return {
    editMessage(message, text) {
      if (busy) return;
      const { setEditingMessageId, setElementSelections, setResponseAnnotations } =
        store.getState();
      setEditingMessageId(message.id);
      const parsed = parseEditedMessage(text);
      setDraft(parsed.composerText);
      setElementSelections(
        parsed.elementSelections.map((selection) => ({ ...selection, id: crypto.randomUUID() })),
      );
      setResponseAnnotations(
        parsed.responseAnnotations.map((annotation) => ({
          ...annotation,
          id: crypto.randomUUID(),
        })),
      );
    },
    cancelMessageEdit() {
      const { setEditingMessageId, setElementSelections, setResponseAnnotations } =
        store.getState();
      setEditingMessageId(null);
      setDraft('');
      setElementSelections([]);
      setResponseAnnotations([]);
    },
  };
}
